import { useRef, useEffect, useState, useCallback } from "react";
import { Home, TrendingUp, DollarSign } from "lucide-react";
import { SpotlightIconPattern } from "./SpotlightIconPattern";

interface SpotlightSpeedToSoldProps {
  salesCountAllTime: number | null;
  salesCountLastYear: number | null;
  avgValueThreeYear: number | null;
  priceRangeMin: number | null;
  priceRangeMax: number | null;
  primaryColor?: string | null;
}

function formatCompactPrice(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${value.toLocaleString()}`;
}

/**
 * Tracks how far through a sticky-scroll container the user has scrolled.
 * Returns 0→1 based on how much scrollable "runway" has been consumed.
 * The extra height creates the "work for it" feeling.
 */
function useStickyProgress(runwayMultiplier = 3) {
  const outerRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const ticking = useRef(false);

  const update = useCallback(() => {
    const el = outerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    // How far the outer container has scrolled past viewport top
    // relative to the total scrollable runway
    const scrolled = -rect.top;
    const runway = rect.height - vh;
    const raw = runway > 0 ? scrolled / runway : 0;
    setProgress(Math.max(0, Math.min(1, raw)));
    ticking.current = false;
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => window.removeEventListener("scroll", onScroll);
  }, [update]);

  return { outerRef, progress, runwayMultiplier };
}

/**
 * Apple-style scroll easing.
 * Content is invisible → fades in with scale + translate → holds → fades out.
 *
 *   0.00–0.40  → fade in  (scale 0.92→1, translate 80→0, opacity 0→1)
 *   0.40–0.55  → fully visible
 *   0.55–1.00  → fade out (translate 0→-80, opacity 1→0)
 */
function getScrollStyles(progress: number) {
  let opacity = 0;
  let translateY = 80;
  let scale = 0.92;

  if (progress < 0.4) {
    const t = progress / 0.4; // 0→1
    const ease = 1 - Math.pow(1 - t, 3); // cubic ease-out
    opacity = ease;
    translateY = 80 * (1 - ease);
    scale = 0.92 + 0.08 * ease;
  } else if (progress < 0.55) {
    opacity = 1;
    translateY = 0;
    scale = 1;
  } else {
    const t = (progress - 0.55) / 0.45; // 0→1
    const ease = t * t; // ease-in
    opacity = 1 - ease;
    translateY = -80 * ease;
    scale = 1;
  }

  return { opacity, transform: `translateY(${translateY}px) scale(${scale})` };
}

export function SpotlightSpeedToSold({
  salesCountAllTime,
  salesCountLastYear,
  avgValueThreeYear,
  priceRangeMin,
  priceRangeMax,
  primaryColor,
}: SpotlightSpeedToSoldProps) {
  const { outerRef, progress } = useStickyProgress(3);

  if (!salesCountAllTime) return null;

  const accentColor = primaryColor ?? "#3B82F6";
  const hasPriceRange = priceRangeMin != null && priceRangeMax != null;
  const styles = getScrollStyles(progress);

  return (
    // Outer: tall container that creates scroll runway (3x viewport height)
    <div ref={outerRef} style={{ height: "300vh" }} className="relative">
      {/* Inner: sticky container pinned to viewport center */}
      <section
        className="sticky top-0 w-full h-screen flex items-center px-6 overflow-hidden relative"
        style={{ background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #f8fafc 100%)" }}
      >
        <SpotlightIconPattern />
        <div
          className="max-w-7xl mx-auto w-full will-change-transform"
          style={styles}
        >
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400 mb-6 text-center">
            Proven Track Record
          </p>

          <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight max-w-4xl mx-auto text-center">
            {salesCountAllTime.toLocaleString()} homes sold and counting
          </h3>

          {salesCountLastYear != null && salesCountLastYear > 0 && (
            <p className="text-lg md:text-xl text-slate-400 mt-4 text-center">
              Including{" "}
              <span className="font-semibold" style={{ color: accentColor }}>
                {salesCountLastYear} sold in the last year
              </span>
            </p>
          )}

          <div className={`grid grid-cols-1 ${hasPriceRange ? "md:grid-cols-3" : "md:grid-cols-2"} gap-8 mt-16`}>
            <div className="rounded-2xl border border-slate-200 bg-white/60 backdrop-blur-sm p-8 text-center shadow-sm">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4" style={{ backgroundColor: `${accentColor}15` }}>
                <Home className="h-6 w-6" style={{ color: accentColor }} />
              </div>
              <p className="text-4xl md:text-5xl font-bold text-slate-900">
                {salesCountAllTime.toLocaleString()}
              </p>
              <p className="text-sm text-slate-400 mt-2">Total Homes Sold</p>
            </div>

            {avgValueThreeYear != null && avgValueThreeYear > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white/60 backdrop-blur-sm p-8 text-center shadow-sm">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4" style={{ backgroundColor: `${accentColor}15` }}>
                  <DollarSign className="h-6 w-6" style={{ color: accentColor }} />
                </div>
                <p className="text-4xl md:text-5xl font-bold text-slate-900">
                  {formatCompactPrice(avgValueThreeYear)}
                </p>
                <p className="text-sm text-slate-400 mt-2">Avg Sale Price</p>
              </div>
            )}

            {hasPriceRange && (
              <div className="rounded-2xl border border-slate-200 bg-white/60 backdrop-blur-sm p-8 text-center shadow-sm">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4" style={{ backgroundColor: `${accentColor}15` }}>
                  <TrendingUp className="h-6 w-6" style={{ color: accentColor }} />
                </div>
                <p className="text-4xl md:text-5xl font-bold text-slate-900">
                  {formatCompactPrice(priceRangeMin!)}
                  <span className="text-slate-300 mx-2">–</span>
                  {formatCompactPrice(priceRangeMax!)}
                </p>
                <p className="text-sm text-slate-400 mt-2">Price Range</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
